/* global Highcharts:false */
/* eslint-disable */
import moment from 'moment';
import { get, maxBy, sumBy } from 'lodash';
import constants from '@/constants';
import { NOTIFICATION_CONTRACT_OWNERSHIP } from '@/services/notifications/proxy';
import store from '@/store';

export const capitalizeFirstLetter = string => string.charAt(0).toUpperCase() + string.slice(1);
export const getRedirectUrl = (path) => {
  const rootPath = process.env.ROOT_PATH.slice(0, -1);
  return `${window.location.protocol}//${window.location.host}${rootPath}${path}`;
};
export const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY hh:mm a';

export const dateToString = (string, fmt = DEFAULT_DATE_FORMAT) => `${moment(string).format(fmt)} (ET)`;
export const priceDisplay = num => num.toFixed(2).toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
export const updateObjectValues = (obj, data, map = {}, setTimestampFor = [NOTIFICATION_CONTRACT_OWNERSHIP]) => {
  const result = { ...obj };
  Object.keys(data).forEach((key) => {
    const timestampKey = 'timestamp';
    const objKey = typeof map[key] !== 'undefined' ? map[key] : key;
    if (
      (typeof result[objKey] !== 'undefined' && objKey !== timestampKey) ||
      (setTimestampFor.includes(data.UpdateType) && objKey === timestampKey)) { // timestamp prop is updated only for certain notifications
      result[objKey] = data[key];
    }
  });

  return result;
};
export const capitalize = input => input.charAt(0).toUpperCase() + input.slice(1);
export const toCents = input => Math.round(100 * input);
export const correctPrice = input => (!isNaN(input) && input < 100 ? input : 0);
export const isNumber = input => !isNaN(parseFloat(input)) && isFinite(input);
export const hasLastTradePrice = (contract) => {
  const value = Number(contract && contract.lastTradePrice);
  return Number.isFinite(value) && Math.round(100 * value) > 0;
};
export const getLeadingTradedContract = (contracts = []) => {
  const results = (contracts || []).filter(hasLastTradePrice);
  return results.length > 0 ? maxBy(results, i => i.lastTradePrice) : null;
};
export const isMarketSingle = market => market && (market.type === 'Single' || market.marketType === 0 || market.MarketType === 0);
export const contractOwnershipNum = contract => contract.userQuantity + contract.userOpenOrdersBuyQuantity + contract.userOpenOrdersSellQuantity;
export const isMarketSettled = market => market && market.status === constants.MARKET_SETTLED;
export const isMarketExpired = market => market && market.status === constants.MARKET_EXPIRED;
export const isMarketSuspended = market => market && market.status === constants.MARKET_SUSPENDED;
export const isMarketMaintenance = market => market && market.status === constants.MARKET_MAINTENANCE;
export const isMarketStatusClosed = market => market && market.status === constants.MARKET_CLOSED;
export const isMarketClosed = market => market && !market.isOpen;
export const isMarketDisabled = market => store.getters['app/hasMaintenance'] || isMarketSettled(market) || isMarketExpired(market) || isMarketSuspended(market) || isMarketMaintenance(market) || isMarketStatusClosed(market);
export const isMarketJourneyDisabled = market => isMarketSettled(market);
export const hasMarketPayout = market => market && (market.userInvestment !== 0 || market.userMaxPayout !== 0);
export const hasMarketHiddenContracts = (market) => {
  const contracts = (market.contracts || market.marketContracts) || [];
  return typeof contracts.find(i => i.hiddenByDefault) !== 'undefined';
};
export const isContractOpen = contract => (typeof contract.isOpen !== 'undefined' && contract.isOpen) ||
  (typeof contract.contractIsOpen !== 'undefined' && contract.contractIsOpen);

export const isHighChartsReady = () => typeof Highcharts !== 'undefined';
import sessionVars from '@/helpers/sessionVars';

export default {};

export function largeNumberDisplay(x, largeThousands = true) {
  let value = x;
  if (value >= 1.0e+6) {
    if ((value / 1.0e+6).toFixed(1).endsWith('.0')) {
      value = `${(value / 1.0e+6).toFixed(0)}M`;
    } else {
      value = `${(value / 1.0e+6).toFixed(1)}M`;
    }
  } else if (largeThousands && value >= 1.0e+5) {
    value = `${(value / 1.0e+3).toFixed(0)}K`;
  } else if (!largeThousands && value >= 1.0e+3) {
    if ((value / 1.0e+3).toFixed(1).endsWith('.0')) {
      value = `${(value / 1.0e+3).toFixed(0)}K`;
    } else {
      value = `${(value / 1.0e+3).toFixed(1)}K`;
    }
  } else {
    value = value.toLocaleString();
  }
  return value;
}

export function elementOffset(el) {
  const rect = el.getBoundingClientRect();
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  return { top: rect.top + scrollTop, left: rect.left + scrollLeft };
}

import { isIos } from '@/mixins/userAgent';

let scrollPosition = 0;
let lockCount = 0;

export function lockScroll() {
  if (lockCount === 0) {
    scrollPosition = window.pageYOffset;
    if (isIos) {
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPosition}px`;
      document.body.style.width = '100%';
    }
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }
  lockCount += 1;
}

export function unlockScroll() {
  if (lockCount <= 0) return;
  lockCount -= 1;
  if (lockCount === 0) {
    document.documentElement.style.removeProperty('overflow');
    document.body.style.removeProperty('overflow');
    if (isIos) {
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('width');
      document.body.style.removeProperty('height');
      window.scrollTo(0, scrollPosition);
      void document.body.offsetHeight;
    }
  }
}

export function marketUserGainLoss(market) {
  const contracts = (market.contracts || market.marketContracts) || [];
  return sumBy(
    contracts,
    (c) => {
      let gainLoss = 0;
      if (c.userPrediction === 0 && c.userOpenOrders !== 0) {
        gainLoss = (1 - c.lastTradePrice - c.userAveragePricePerShare) * c.userQuantity;
      } else {
        gainLoss = (c.lastTradePrice - c.userAveragePricePerShare) * c.userQuantity;
      }
      return gainLoss;
    },
  );
}

export const hasNoPositionInAMarketBesidesLoadedContract = ({ market, contract }) => {
  if (!market || !contract) return false;
  const contracts = (market.contracts || market.marketContracts) || [];
  const contractsWithout = contracts.filter((c) => c.contractId !== contract.contractId);
  if (contractsWithout.length === 0) return false;

  return contractsWithout.some((c) => c.userPrediction === 0 && c.userQuantity > 0);
}

export const smartbtoa = input => btoa(unescape(encodeURIComponent(input)));
export const smartatob = input => decodeURIComponent(escape(atob(input)));

export const groupByKeepOrder = (arr, prop) => {
  const newArr = [];
  const wrapObj = {};

  arr.forEach((item) => {
    let propName = item[prop];
    if (propName) {
      propName = propName.toString();
      if (!wrapObj[propName]) {
        wrapObj[propName] = [];
        newArr.push(wrapObj[propName]);
      }
      wrapObj[propName].push(item);
    }
  });

  return newArr;
};

export function roundToTwo(value) {
  return (Math.round(value * 100) / 100);
}

export function roundDown(value) {
  return (Math.floor(value * 100) / 100);
}

export function roundTop(value) {
  return (Math.ceil(value * 100) / 100);
}

export const getCardTypeForNumber = (value) => {
  if (/^(?:4\d{12}(?:\d{3})?)$/.test(value)) return constants.CARDS.VISA;
  if (/^(?:3[47]\d{13})$/.test(value)) return constants.CARDS.AMERICAN_EXPRESS;
  if (/^6(?:011\d{12,15}|5\d{14,17}|4[4-9]\d{13,16}|22(?:1(?:2[6-9]|[3-9]\d)|[2-8]\d{2}|9(?:[01]\d|2[0-5]))\d{10,13})$/.test(value)) return constants.CARDS.DISCOVER;
  if (/^(?:5[1-5]\d{14})$/.test(value)) return constants.CARDS.MASTERCARD;
  return constants.CARDS.BLANK;
};

export const getOrdinalSuffix = (value) => {
  const symbols = ['th', 'st', 'nd', 'rd'];
  const v = value % 100;
  return (symbols[(v - 20) % 10] || symbols[v] || symbols[0]);
};

const loadedScripts = {}
export const loadScript = (url, windowVariable) => {
  if (!loadedScripts[url]) {
    loadedScripts[url] = new Promise((resolve, reject) => {
      let script = document.createElement('script')
      script.type = 'text/javascript'
      script.src = url
      script.addEventListener('load', () => resolve(windowVariable ? get(window, windowVariable) : undefined), false)
      script.addEventListener('error', e => reject(e), false)
      document.body.appendChild(script)
    })
  }
  return loadedScripts[url]
}

export const parseQuery = (queryString) => {
  const query = {};
  const a = ((queryString[0] === '?' || queryString[0] === '#') ? queryString.substr(1) : queryString).split('&');
  for (let i = 0; i < a.length; i += 1) {
    const b = a[i].split('=');
    query[decodeURIComponent(b[0])] = decodeURIComponent(b[1] || '');
  }
  return query;
}
export const checkIfKeyExist = (objectName, keyName) => Object.keys(objectName).some(key => key === keyName);

function getExtendedUrlWithSessionVarsInQuery(url) {
  const data = sessionVars.params();
  const keys = Object.keys(data);
  if (keys.length === 0) {
    return encodeURIComponent(url);
  }

  try {
    const urlObject = new URL(url);
    keys.forEach(key => {
      const value = data[key];
      if (value !== undefined && value !== null) {
          urlObject.searchParams.set(key, String(value));
      }
    });
    return encodeURIComponent(urlObject.toString());
  } catch (e) {
    return encodeURIComponent(url);
  }
}

export const loginUrl = ({ returnTo = null } = {}) => {
  const url = returnTo || (window.location.pathname + window.location.search)
  const isDev = process.env.NODE_ENV === 'development';
  const finalUrl = isDev ? window.location.href : url;
  const returnUrl = getExtendedUrlWithSessionVarsInQuery(finalUrl);
  return `${process.env.REAL_API_URL}auth/login?returnUrl=${returnUrl}`;
}
export const signUpUrl = ({ returnTo = null } = {}) => {
  const url = returnTo || (window.location.pathname + window.location.search)
  const isDev = process.env.NODE_ENV === 'development';
  const finalUrl = isDev ? window.location.href : url;
  const returnUrl = getExtendedUrlWithSessionVarsInQuery(finalUrl);
  return `${process.env.REAL_API_URL}auth/register?returnUrl=${returnUrl}`;
}

export const editAccountDetailsUrl = () => {
  const url = window.location.pathname + window.location.search
  const isDev = process.env.NODE_ENV === 'development';
  const finalUrl = isDev ? window.location.href : url;
  const returnUrl = getExtendedUrlWithSessionVarsInQuery(finalUrl);
  return `${process.env.REAL_API_URL}auth/manage?returnUrl=${returnUrl}`;
}

export const verifyDetailsUrl = () => {
  const url = window.location.pathname + window.location.search
  const isDev = process.env.NODE_ENV === 'development';
  const finalUrl = isDev ? window.location.href : url;
  const returnUrl = getExtendedUrlWithSessionVarsInQuery(finalUrl);
  return `${process.env.REAL_API_URL}auth/profile?returnUrl=${returnUrl}`;
}

export const logoutUrl = ({ routeRequiresAuth = true } = {}) => {
  const url = routeRequiresAuth ? '/' : (window.location.pathname + window.location.search);
  const isDev = process.env.NODE_ENV === 'development';
  const finalUrl = isDev ? window.location.href : url;
  const returnUrl = getExtendedUrlWithSessionVarsInQuery(finalUrl);
  return `${process.env.REAL_API_URL}auth/logout?returnUrl=${returnUrl}`;
}

export const changeAvatarImageUrl = () =>  {
  const url = window.location.pathname + window.location.search
  const isDev = process.env.NODE_ENV === 'development';
  const finalUrl = isDev ? window.location.href : url;
  const returnUrl = getExtendedUrlWithSessionVarsInQuery(finalUrl);
  return `${process.env.REAL_API_URL}auth/avatar?returnUrl=${returnUrl}`;
}

export const registerPersonalDetailsUrl = () =>  {
  const url = window.location.pathname + window.location.search;
  const isDev = process.env.NODE_ENV === 'development';
  const finalUrl = isDev ? window.location.href : url;
  const returnUrl = getExtendedUrlWithSessionVarsInQuery(finalUrl);
  return `${process.env.REAL_API_URL}auth/registration?returnUrl=${returnUrl}`;
}


export const getAssetPath = (path) => `${process.env.ASSETS_CDN_URL}${path}`;

export const isVisibleInViewport = (element) => {
  	const elementStyle = window.getComputedStyle(element);
	//Particular cases when the element is not visible at all
	if (
	elementStyle.height == '0px' ||
	elementStyle.display == 'none' ||
	elementStyle.opacity == '0' ||
        elementStyle.visibility == 'hidden' ||
  	elementStyle.clipPath == 'circle(0px at 50% 50%)' ||
        elementStyle.transform == 'scale(0)' ||
        element.hasAttribute('hidden')
	) {
		return false;
	}

    	const rect = element.getBoundingClientRect();

  	//Overlapping strict check
  	const baseElementLeft = rect.left;
	const baseElementTop = rect.top;

  	const elementFromStartingPoint = document.elementFromPoint(baseElementLeft,baseElementTop);

    	if (elementFromStartingPoint != null && !element.isSameNode(elementFromStartingPoint)) {
      		const elementZIndex = elementStyle.zIndex;
      		const elementOverlappingZIndex = window.getComputedStyle(elementFromStartingPoint).zIndex;
      		if (Number(elementZIndex) < Number(elementOverlappingZIndex)){
        		return false;
      		}

		if (elementZIndex === '' && elementOverlappingZIndex === '') {
			/**
        		If two positioned elements overlap without a z-index specified, the element
			positioned last in the HTML code will be shown on top
        		**/
			if (element.compareDocumentPosition(elementFromStartingPoint) & Node.DOCUMENT_POSITION_FOLLOWING) {
			  return false;
			}
		}
    }

    return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
}
export const stripTags = (string) =>
  (string || "").replace(/(<([^>]+)>)/gi, "");

export const isEmptyHtml = (html) => {
  if (!html) return true;
  const stripped = html.replace(/<[^>]*>?/gm, '').trim();
  return stripped.length === 0;
};
export const uuidv4 = () => {
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
    (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)
  );
}



// WEBPACK FOOTER //
// ./src/utils/helpers.js